import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { getFilteredFinSobRows, getRevenueAndSobBaseline } from '../../../../lib'
import { isActualsFinSobRow } from '../../../../lib/revenueAndSob/isActualsFinSobRow'
import { useEffectiveActualsSync } from '../../../../model/hooks'
import { useCockpitSettings } from '../../../../model/hooks/useCockpitSettings'
import {
  selectCockpitOffering,
  selectCockpitPeriods,
  selectVisibleCockpitFinSobRows,
} from '../../../../model/selectors'
import type { IEnrichedCockpitFinSobRow } from '../../../../model/types'

const toKey = (partnerId: string, offering: string | null) => `${partnerId}|${offering ?? ''}`

/**
 * Each partner's last-year sales, as the Financial & SOB tile reads its baseline: the period and
 * metric the deck stands on, the offering total. Read in USD whatever the deck's currency, since
 * partners in different local currencies are compared. Only ranks partners; never shown.
 *
 * Returns the baseline of one partner's offering, or of the partner across the deck's offering
 * where none is named.
 */
export const usePartnerBaselines = () => {
  const rows = useSelector(selectVisibleCockpitFinSobRows)
  const deckOffering = useSelector(selectCockpitOffering)
  const periods = useSelector(selectCockpitPeriods)
  const { actualsType, isYtd } = useCockpitSettings()
  const { fYear, currentFQuarter, fQuarterActualsPercentage } = useEffectiveActualsSync()

  const baselines = useMemo(() => {
    const groups = new Map<string, IEnrichedCockpitFinSobRow[]>()
    const add = (key: string, row: IEnrichedCockpitFinSobRow) =>
      groups.set(key, [...(groups.get(key) ?? []), row])

    getFilteredFinSobRows({
      rows,
      offering: deckOffering,
      periods,
      currentFQuarter,
      category: null,
    })
      .filter(isActualsFinSobRow)
      .forEach((row) => {
        add(toKey(row.partnerId, row.offering.trim()), row)
        add(toKey(row.partnerId, null), row)
      })

    const sales = new Map<string, number>()

    groups.forEach((groupRows, key) =>
      sales.set(
        key,
        getRevenueAndSobBaseline({
          rows: groupRows,
          selectedFYear: fYear,
          actualsType,
          isLocalCurrency: false,
          currentFQuarter,
          fQuarterActualsPercentage: isYtd ? fQuarterActualsPercentage : null,
        }).sales ?? 0
      )
    )

    return sales
  }, [
    actualsType,
    currentFQuarter,
    deckOffering,
    fQuarterActualsPercentage,
    fYear,
    isYtd,
    periods,
    rows,
  ])

  return useCallback(
    (partnerId: string, offering: string | null) =>
      baselines.get(
        toKey(partnerId, offering && offering !== ProgramTypes.Overall ? offering : null)
      ) ?? 0,
    [baselines]
  )
}
